/**
 * Retention is a closed choice (D-CP23, BL10): 7 / 30 / 60 / 90 / 365 days — so a typo can never delete a
 * year of data. Invoices and ledger rows have a legal floor of 365 days (R13), enforced HERE as well as by
 * billing: a value below the floor is not even offered as an enabled choice.
 */

export const RETENTION_CLOSED_SET = [7, 30, 60, 90, 365] as const;

/** Classes the console refuses to shorten below the legal minimum, whatever the server's row says. */
export const LEGAL_FLOOR_CLASSES = ['invoices', 'ledger'] as const;
export const LEGAL_FLOOR_DAYS = 365;

/** The effective floor: the policy's `floor_days`, and never below 365 for invoices / ledger. */
export function retentionFloor(dataClass: string, floorDays: number | null | undefined): number | null {
  const legal = (LEGAL_FLOOR_CLASSES as readonly string[]).includes(dataClass) ? LEGAL_FLOOR_DAYS : null;
  const server = typeof floorDays === 'number' && floorDays > 0 ? floorDays : null;
  if (legal === null) return server;
  return Math.max(legal, server ?? 0);
}

export interface RetentionOption {
  value: number;
  disabled: boolean;
  reason: string | null;
}

/**
 * The choices for one class: the server's `allowed` list INTERSECTED with the closed set (a value outside
 * the closed set is never offered, even if a server sent one), each disabled below the floor.
 */
export function retentionOptions(allowed: readonly number[] | null | undefined, floor: number | null): RetentionOption[] {
  const source = allowed && allowed.length > 0 ? allowed : RETENTION_CLOSED_SET;
  return RETENTION_CLOSED_SET.filter((d) => source.includes(d)).map((value) => {
    const belowFloor = floor !== null && value < floor;
    return {
      value,
      disabled: belowFloor,
      reason: belowFloor ? `Below the ${floor}-day floor for this data class` : null,
    };
  });
}

/** Null when `days` is a legal choice for the class; otherwise why not. */
export function validateRetention(days: number, allowed: readonly number[] | null | undefined, floor: number | null): string | null {
  const option = retentionOptions(allowed, floor).find((o) => o.value === days);
  if (!option) return `${days} days is not one of ${RETENTION_CLOSED_SET.join(' / ')}.`;
  if (option.disabled) return option.reason;
  return null;
}

/** The picker: every legal value visible at once, below-floor values disabled with the reason. */
export function RetentionPicker({
  dataClass, value, onChange, allowed, floorDays, disabled = false,
}: {
  dataClass: string;
  value: number;
  onChange: (days: number) => void;
  allowed?: readonly number[] | null;
  floorDays?: number | null;
  disabled?: boolean;
}) {
  const floor = retentionFloor(dataClass, floorDays);
  const options = retentionOptions(allowed, floor);

  return (
    <div className="segmented" role="radiogroup" aria-label={`Retention for ${dataClass}`}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          className={`segmented-item${option.value === value ? ' is-active' : ''}${option.disabled ? ' is-below-floor' : ''}`}
          disabled={disabled || option.disabled}
          title={option.reason ?? undefined}
          onClick={() => onChange(option.value)}
        >
          {option.value}d
        </button>
      ))}
    </div>
  );
}
